import { useState, useCallback } from 'react';

import { createContext } from 'use-context-selector';

import api from '../services/api';
import { ClientConfig } from '../types/types';

interface AppContextI {
  clientConfig: ClientConfig | null;
  refetchClientConfig(): void;
  saveCarouselScrollIndex(carouselId: string, carouselIndex: number): void;
  getCarouselIndex(carouselId: string): number | undefined;
  openSeasonsList(tvId: string, seasonNumber: number): void;
  closeSeasonsList(tvId: string): void;
  getCurrentSeasonOpen(tvId: string): number;
  openedSeasonsLists: { tvId: string; seasonNumber: number }[];
}

interface PropTypes {
  value?: AppContextI;
  children: React.ReactNode;
}

interface Carousel {
  id: string;
  index: number;
}

const AppContext = createContext<AppContextI>(null as any);

export default AppContext;

export const AppProvider: React.FC<PropTypes> = ({ children }) => {
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [carousels, setCarousels] = useState<Carousel[]>([]);
  const [openedSeasonsLists, setOpenedSeasonsLists] = useState<
    { tvId: string; seasonNumber: number }[]
  >([]);

  async function fetchClientConfig() {
    try {
      const response = await api.get('/v1/client-config');

      setClientConfig(response.data.data);
    } catch (err) {}
  }

  function refetchClientConfig() {
    fetchClientConfig();
  }

  const saveCarouselScrollIndex = useCallback(
    (carouselId: string, carouselIndex: number) => {
      setCarousels(prevState => {
        const exists = prevState.find(carousel => carousel.id === carouselId);

        if (exists) {
          return prevState.map(carousel => {
            if (carousel.id === carouselId) {
              return {
                id: carouselId,
                index: carouselIndex
              };
            }

            return carousel;
          });
        } else {
          return [...prevState, { id: carouselId, index: carouselIndex }];
        }
      });
    },
    []
  );

  function getCarouselIndex(carouselId: string) {
    return carousels.find(carousel => carousel.id === carouselId)?.index;
  }

  function openSeasonsList(tvId: string, seasonNumber: number) {
    setOpenedSeasonsLists(prevState => {
      if (prevState.find(seasonListItem => seasonListItem.tvId === tvId)) {
        return prevState.map(seasonListItem =>
          seasonListItem.tvId
            ? { ...seasonListItem, seasonNumber }
            : seasonListItem
        );
      }
      return [...prevState, { tvId: tvId, seasonNumber }];
    });
  }

  function closeSeasonsList(tvId: string) {
    setOpenedSeasonsLists(prevState =>
      prevState.filter(seasonListItem => seasonListItem.tvId !== tvId)
    );
  }

  function getCurrentSeasonOpen(tvId: string) {
    return (
      openedSeasonsLists.find(seasonListItem => seasonListItem.tvId === tvId)
        ?.seasonNumber || 1
    );
  }

  return (
    <AppContext.Provider
      value={{
        clientConfig,
        refetchClientConfig,
        saveCarouselScrollIndex,
        getCarouselIndex,
        openSeasonsList,
        closeSeasonsList,
        getCurrentSeasonOpen,
        openedSeasonsLists
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
